/**
 * Plan status — pure helpers, no Firebase.
 *
 * A student referred for special education has no plan document yet, but they
 * do have a case manager, a schedule, and an initial meeting someone has to
 * hold. That state is modelled as `app.studentData.planStatus = 'initial'`
 * ALONGSIDE the plan they are being assessed for, not as a separate plan value.
 *
 * Why a status and not `plan: 'Initial-IEP'`: `plan` is read by Firestore rules
 * (sped-chair read/edit and list-query validation), by the sped-chair student
 * query, and by the meeting calendar — all of which compare against the literal
 * 'IEP' / '504'. An unrecognized value makes the student INVISIBLE to every one
 * of those, including the meeting calendar they need to appear in. A status
 * flag leaves all of that working and lets the few places that should behave
 * differently opt out explicitly.
 */

export const PLAN_STATUS_ACTIVE = 'active'
export const PLAN_STATUS_INITIAL = 'initial'

/**
 * A stored `plan` value, with display placeholders read as empty.
 * getDisplayValue's '—' fallback has been saved as data before; a plan of
 * '—' must compare as "no plan", not as a third plan letter.
 * @param {*} value
 * @returns {string}
 */
export function storedPlanValue(value) {
  const plan = String(value ?? '').trim()
  return plan === '—' || plan === '-' ? '' : plan
}

/** Statuses that may be stored; anything else reads as active. */
export const PLAN_STATUSES = [PLAN_STATUS_ACTIVE, PLAN_STATUS_INITIAL]

/**
 * Read a student's plan status. Absent means active — the overwhelming
 * majority of records predate this field and are established plans.
 * @param {object} student
 * @returns {string}
 */
export function getPlanStatus(student) {
  const raw = String(student?.app?.studentData?.planStatus || '')
    .trim()
    .toLowerCase()
  return raw === PLAN_STATUS_INITIAL ? PLAN_STATUS_INITIAL : PLAN_STATUS_ACTIVE
}

/** True when the student is awaiting an initial plan meeting. */
export function isInitialPlan(student) {
  return getPlanStatus(student) === PLAN_STATUS_INITIAL
}

/**
 * Badge text for the plan tag, e.g. "IEP · Initial".
 * @param {string} plan
 * @param {string} status
 * @returns {string}
 */
export function planDisplayLabel(plan, status) {
  const label = String(plan || '').trim()
  if (String(status || '').toLowerCase() !== PLAN_STATUS_INITIAL) return label
  return label ? `${label} · Initial` : 'Initial'
}

/**
 * Explains why an initial student has no dates, so the empty review /
 * reevaluation / meeting badges read as expected rather than as a lapse.
 */
export const INITIAL_PLAN_DATE_NOTE = 'Initial — no plan yet, dates set at the initial meeting'

/**
 * Split a student list into established plans and pending initials.
 * @param {object[]} students
 * @returns {{ total: number, active: number, initial: number }}
 */
export function splitCaseloadCounts(students = []) {
  let initial = 0
  for (const student of students) {
    if (isInitialPlan(student)) initial++
  }
  const total = students.length
  return { total, active: total - initial, initial }
}

/**
 * Caseload count as "25 (I-1)" — established students, with pending initials
 * called out separately. Initials are assigned to the case manager (that is how
 * the meeting and feedback forms reach them) but are not yet qualified, so they
 * are not folded into the headline number.
 *
 * @param {{ active: number, initial: number }} counts
 * @returns {string}
 */
export function formatCaseloadCount({ active = 0, initial = 0 } = {}) {
  return initial ? `${active} (I-${initial})` : String(active)
}

/** Tooltip spelling out what the "(I-n)" suffix means. */
export function caseloadCountTitle({ active = 0, initial = 0 } = {}) {
  if (!initial) return `${active} student(s) on caseload`
  return (
    `${active} student(s) on caseload, plus ${initial} initial(s) awaiting a first ` +
    'plan meeting — assigned to this case manager but not yet qualified.'
  )
}
